import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Pagination,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreateIcon from '@mui/icons-material/Create';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { searchPetitions } from '../services/api';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import RSSButton from './RSSButton';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

export default function PetitionsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, pageSize = perPage) => {
    const baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    const q = buildQueryWithFilters(baseQuery, filters);

    setLoading(true);
    try {
      const data = await searchPetitions(q, newPage, pageSize);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const facetToField = (facetName) => {
    const mapping = {
      'petition_types': 'type',
      'statuses': 'status',
      'years': 'year',
      'response_status': 'is_responded'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'petition_types': 'Petition Type',
      'statuses': 'Status',
      'years': 'Year',
      'response_status': 'Response Status'
    };
    return names[facetName] || facetName;
  };

  const formatFacetValue = (facetName, value) => {
    if (facetName === 'response_status') {
      return value === true || value === 'true' ? 'Responded' : 'No Response';
    }
    return value;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'e-petition': return 'primary';
      case 'paper': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'success';
      case 'Closed': return 'default';
      case 'Presented': return 'info';
      case 'Responded': return 'primary';
      default: return 'default';
    }
  };

  const formatSignatures = (count) => {
    if (!count) return '0';
    return count.toLocaleString();
  };

  const renderFacets = () => {
    if (!facets || Object.keys(facets).length === 0) return null;

    const facetOrder = sortFacetsBooleansFirst(['petition_types', 'statuses', 'years', 'response_status'], facets);

    return (
      <Box sx={{ mb: 3 }}>
        {facetOrder.map(facetName => {
          const facetData = facets[facetName];
          if (!facetData || facetData.length === 0) return null;
          const field = facetToField(facetName);

          return (
            <Accordion key={facetName} defaultExpanded={facetName === 'petition_types' || facetName === 'statuses'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {facetDisplayName(facetName)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense sx={{ py: 0 }}>
                  {facetData.slice(0, 20).map((item) => (
                    <ListItem
                      key={String(item.value)}
                      dense
                      sx={{ py: 0.5, cursor: 'pointer' }}
                      onClick={() => handleFacetClick(field, item.value)}
                    >
                      <Checkbox
                        edge="start"
                        checked={activeFilters[field]?.includes(item.value) || false}
                        size="small"
                        sx={{ py: 0 }}
                      />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">{formatFacetValue(facetName, item.value)}</Typography>
                            <Chip label={item.count} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const renderActiveFilters = () => {
    const allFilters = Object.entries(activeFilters).flatMap(([field, values]) =>
      values.map(value => ({ field, value }))
    );

    if (allFilters.length === 0) return null;

    return (
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">Active filters:</Typography>
        {allFilters.map(({ field, value }) => (
          <Chip
            key={`${field}-${value}`}
            label={`${field}: ${value}`}
            size="small"
            onDelete={() => handleFacetClick(field, value)}
          />
        ))}
        <Button size="small" onClick={clearAllFilters}>Clear all</Button>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Petitions
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search {results?.total?.toLocaleString() || '5,055'} petitions from the House of Representatives, including e-petitions and paper petitions.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search petitions..."
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            startIcon={<SearchIcon />}
            disabled={loading}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Paper>

      {renderActiveFilters()}

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          {renderFacets()}
        </Grid>

        <Grid item xs={12} md={9}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && results && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Found {results.total?.toLocaleString()} petitions
                  {results.took_ms && ` (${results.took_ms}ms)`}
                </Typography>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <ExportCSVButton
                  dataType="petitions"
                  query={query || '*'}
                  totalResults={results.total}
                />
                <RSSButton collection="petitions" query={query || '*'} />
              </Box>

              <List>
                {results.hits?.map((petition, index) => (
                  <React.Fragment key={petition.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="resultTitle" sx={{ mb: 0.5 }}>
                            {petition.title || petition.petition_number}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                            <Chip
                              label={petition.type}
                              size="small"
                              color={getTypeColor(petition.type)}
                              variant="outlined"
                              icon={petition.type === 'e-petition' ? <HowToVoteIcon /> : <CreateIcon />}
                            />
                            <Chip
                              label={petition.status}
                              size="small"
                              color={getStatusColor(petition.status)}
                              variant="outlined"
                            />
                            {petition.is_responded && (
                              <Chip
                                label="Responded"
                                size="small"
                                color="success"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                            {petition.signature_count > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                {formatSignatures(petition.signature_count)} signatures
                              </Typography>
                            )}
                          </Box>
                          {petition.request_date && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Requested: {formatDate(petition.request_date)}
                            </Typography>
                          )}
                          {petition.presented_mp && (
                            <Typography variant="body2" color="text.secondary">
                              Presented by: {petition.presented_mp}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {petition.reason && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                            Reason:
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {petition.reason}
                          </Typography>
                        </Box>
                      )}

                      {petition.request && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                            Request:
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {petition.request}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        {petition.source_url && (
                          <Link
                            href={petition.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                          >
                            <OpenInNewIcon fontSize="small" />
                            View on APH
                          </Link>
                        )}
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        {petition.petition_number}
                      </Typography>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>

              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}

          {!loading && !results && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Enter a search term or click Search to browse all petitions.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
